// ============================================================================
// MarketWalletHolds — активные резервы (холды) личного счёта.
// Сумма резерва уменьшает «доступно», но не списывается до подтверждения.
// Список карточек, без таблиц.
// ============================================================================

import { Lock } from 'lucide-react';
import { formatValue } from '@/lib/format';
import {
  WALLET_HOLD_STATE_LABELS,
  type MarketWalletHold,
} from '@/lib/marketWallet';

function money(value: number): string {
  return formatValue(value, 'number:0;group', 'numeric');
}

function holdStateClass(state: string): string {
  if (state === 'captured') return 'border-border bg-muted text-foreground';
  if (state === 'released') return 'border-border bg-muted text-muted-foreground';
  return 'border-amber-500/30 bg-amber-500/5 text-amber-700';
}

interface MarketWalletHoldsProps {
  holds: MarketWalletHold[];
  currency?: string;
}

export function MarketWalletHolds({ holds, currency }: MarketWalletHoldsProps) {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">Резервы</h2>
        {holds.length > 0 && (
          <span className="text-[11px] text-muted-foreground">{holds.length}</span>
        )}
      </div>

      {holds.length === 0 ? (
        <div className="flex items-center gap-2 rounded-2xl border border-dashed bg-card/50 px-3.5 py-3 text-xs text-muted-foreground">
          <Lock className="h-4 w-4 shrink-0" />
          Активных резервов нет — весь доступный остаток свободен.
        </div>
      ) : (
        <ul className="space-y-2">
          {holds.map((hold) => {
            const stateLabel = WALLET_HOLD_STATE_LABELS[hold.state] || hold.state;
            const expires = hold.expiresAt
              ? formatValue(hold.expiresAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp')
              : '';
            return (
              <li key={hold.id} className="rounded-2xl border bg-card p-3 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-700">
                    <Lock className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium">Резерв средств</div>
                      <div className="shrink-0 text-sm font-semibold tabular-nums">
                        {money(hold.amount)}
                        {currency && (
                          <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                            {currency}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${holdStateClass(hold.state)}`}>
                        {stateLabel}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {expires ? `Действует до ${expires}` : 'Бессрочный резерв'}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
